import { createContext, useContext, useState, type ReactNode } from 'react'

/**
 * Persona view simulation.
 *
 * This Platform Admin tool can *preview* what an RD Admin (a customer's own
 * admin, who in production uses a scoped version inside Ros) would see. The
 * toggle is a VIEW FILTER ONLY — it hides surfaces an RD Admin wouldn't have,
 * so we can demonstrate the boundary. It is NOT a security control: this app
 * still holds the service key and can do everything regardless of the toggle.
 * Real RD-Admin enforcement happens server-side in Ros via the JWT +
 * requireRdAdmin. The toggle is labeled "(preview)" so no one mistakes it for
 * access control.
 */

export type Persona = 'platform' | 'rd'

interface PersonaState {
  persona: Persona
  setPersona: (p: Persona) => void
  /** Whether the current persona can see platform-wide surfaces (org/RD creation, global registries). */
  canPlatform: boolean
  /** In RD-Admin preview, the RD the user is "administering". null = none picked yet. */
  activeRdId: string | null
  setActiveRdId: (id: string | null) => void
}

const PersonaContext = createContext<PersonaState | null>(null)

export function PersonaProvider({ children }: { children: ReactNode }) {
  const [persona, setPersonaState] = useState<Persona>(
    (localStorage.getItem('authAdminPersona') as Persona) || 'platform',
  )
  const [activeRdId, setActiveRdIdState] = useState<string | null>(
    localStorage.getItem('authAdminActiveRd') || null,
  )
  function setPersona(p: Persona) {
    localStorage.setItem('authAdminPersona', p)
    setPersonaState(p)
  }
  function setActiveRdId(id: string | null) {
    if (id) localStorage.setItem('authAdminActiveRd', id)
    else localStorage.removeItem('authAdminActiveRd')
    setActiveRdIdState(id)
  }
  return (
    <PersonaContext.Provider value={{ persona, setPersona, canPlatform: persona === 'platform', activeRdId, setActiveRdId }}>
      {children}
    </PersonaContext.Provider>
  )
}

export function usePersona(): PersonaState {
  const ctx = useContext(PersonaContext)
  if (!ctx) throw new Error('usePersona must be used within PersonaProvider')
  return ctx
}
